import { useEffect, useRef, useState } from 'react';

import type { SelectedService, Service } from '@/types/booking';
import { isWeb } from '@/utils/getPlatform';

interface UsePreselectServiceParams {
  isInitializingCart: boolean;
  preselectId: string | undefined;
  services: Service[];
  allowMultipleServices: boolean;
  barberIdOrRoute: string | undefined;
  addService: (barberId: string | undefined, service: SelectedService) => void;
  changeService: (
    barberId: string | undefined,
    service: SelectedService,
  ) => void;
}

interface UsePreselectServiceResult {
  /** True once the addService/changeService mutation has rejected — callers
   * waiting on the service to land on the cart should stop waiting and fall
   * back to their own error/redirect handling instead of hanging forever. */
  failed: boolean;
}

export function usePreselectService({
  isInitializingCart,
  preselectId,
  services,
  allowMultipleServices,
  barberIdOrRoute,
  addService,
  changeService,
}: UsePreselectServiceParams): UsePreselectServiceResult {
  const hasAutoSelectedRef = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (isInitializingCart || !preselectId || hasAutoSelectedRef.current)
      return;
    // Mark as attempted immediately — services come from useSuspenseQuery and
    // won't change, so retrying on an unknown ID would spin forever.
    hasAutoSelectedRef.current = true;
    const service = services.find((s) => s.id === preselectId);
    if (!service) return;
    const serviceToAdd: SelectedService = {
      id: service.id,
      name: service.name,
      duration: service.duration,
      price: service.cost,
      categoryId: service.categories?.[0]?.id,
    };
    const result = allowMultipleServices
      ? addService(barberIdOrRoute, serviceToAdd)
      : changeService(barberIdOrRoute, serviceToAdd);
    // addService/changeService return void in their type signature but can
    // return a mutation promise at runtime — wrap in Promise.resolve so a
    // rejection surfaces here instead of becoming an unhandled rejection, and
    // callers waiting on the cart (e.g. a deep-link screen) can stop waiting.
    Promise.resolve(result).catch(() => setFailed(true));
    if (isWeb()) {
      const url = new URL(window.location.href);
      url.searchParams.delete('preselect');
      window.history.replaceState(null, '', url.toString());
    }
  }, [
    isInitializingCart,
    preselectId,
    services,
    allowMultipleServices,
    addService,
    changeService,
    barberIdOrRoute,
  ]);

  return { failed };
}
